class LdJsonProduct {
  constructor(selectedPrice) {
    this.productJsonEl = document.querySelector('[data-product-json]');
    this.selectedPrice = selectedPrice;
    if (!this.productJsonEl) return;

    this.productData = JSON.parse(this.productJsonEl.textContent);
    this.createLdJsonScript();
  }

  createLdJsonScript() {
    if (document.getElementById('ld-json-product')) {
      return;
    }
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'ld-json-product';

    const productUrl = new URL(window.location.href);
    // for PDP we are always using first variant
    this.selectedVariant = this.productData.variants[0];

    const descriptionContainer = document.createElement('div');
    descriptionContainer.innerHTML = this.productData.description;

    const { qty, sub } = this._getValidatedUrlParams();
    // #28: default to the variant's actual price so the offer is never emitted as 0
    // (invalid structured data). NOTE: the constructor is called with a DOM element
    // (querySelector('[data-price]')), so only parse selectedPrice when it is a
    // string — otherwise .replace() throws and aborts the <variant-radios> upgrade.
    let formattedPrice = ((this.selectedVariant.price * qty) / 100).toFixed(2);
    if (typeof this.selectedPrice === 'string') {
      formattedPrice = this._parseMoney(this.selectedPrice);
    }

    const twoWeeksFromNow = new Date(new Date().setDate(new Date().getDate() + 14)).toISOString().split('T')[0];

    const ldJsonData = {
      '@context': 'http://schema.org/',
      '@type': 'Product',
      '@id': this.productData.handle,
      name: this.productData.title,
      url: window.location.href,
      image: [this.productData.featured_image],
      description: (descriptionContainer.textContent || descriptionContainer.innerText || '').replace(/\s+/g, ' ').trim(),
      sku: this.selectedVariant.sku,
      offers: [
        {
          '@type': 'Offer',
          sku: this.selectedVariant.sku,
          gtin13: this.selectedVariant.barcode,
          availability: this.selectedVariant.available ? 'http://schema.org/InStock' : 'http://schema.org/OutOfStock',
          price: formattedPrice,
          priceCurrency: window.Shopify.currency.active,
          url: window.location.href,
          priceValidUntil: twoWeeksFromNow,
          hasMerchantReturnPolicy: {
            '@type': 'MerchantReturnPolicy',
            applicableCountry: 'DE',
            returnPolicyCategory: 'https://schema.org/MerchantReturnFiniteReturnWindow',
            merchantReturnDays: 14,
            returnMethod: 'https://schema.org/ReturnByMail',
            returnFees: 'https://schema.org/FreeReturn',
          },
          priceSpecification: [
            {
              '@type': 'UnitPriceSpecification',
              name: 'UVP',
              price: ((this.selectedVariant.price * qty) / 100).toFixed(2),
              priceCurrency: 'EUR',
              referenceQuantity: {
                '@type': 'QuantitativeValue',
                value: qty,
                unitCode: 'C62',
              },
            },
            {
              '@type': 'UnitPriceSpecification',
              name: 'Angebot',
              price: formattedPrice,
              priceCurrency: 'EUR',
              validFrom: new Date().toISOString().split('T')[0],
              validThrough: twoWeeksFromNow,
              referenceQuantity: {
                '@type': 'QuantitativeValue',
                value: qty,
                unitCode: 'C62',
              },
            },
          ],
        },
      ],
    };

    const ratingJsonEl = document.getElementById('ld-json-rating');
    const ratingData = ratingJsonEl && JSON.parse(ratingJsonEl.textContent);
    if (ratingData?.aggregateRating) {
      ldJsonData.aggregateRating = ratingData.aggregateRating;
    }

    if (sub === 0 || sub === 1) {
      ldJsonData.offers[0].shippingDetails = this._getShippingDetails(sub);
    }

    script.innerHTML = JSON.stringify(ldJsonData, null, 2);
    document.body.prepend(script);
    this.scriptEl = script;
  }

  updateLdJson(selectedPrice) {
    if (!this.scriptEl) return;
    const { qty } = this._getValidatedUrlParams();
    const formattedPrice = this._parseMoney(selectedPrice);
    const ldJsonData = JSON.parse(this.scriptEl.innerHTML);
    ldJsonData.offers[0].price = formattedPrice;
    ldJsonData.offers[0].priceSpecification[0].price = ((this.selectedVariant.price * qty) / 100).toFixed(2);
    ldJsonData.offers[0].priceSpecification[1].price = formattedPrice;
    ldJsonData.offers[0].priceSpecification[0].referenceQuantity.value = qty;
    ldJsonData.offers[0].priceSpecification[1].referenceQuantity.value = qty;
    this.scriptEl.innerHTML = JSON.stringify(ldJsonData, null, 2);
  }

  _parseMoney(value) {
    // Robust German money parse: drop currency + thousands separators, comma -> dot,
    // so amounts >= 1.000 (e.g. "1.234,56 €") do not yield an invalid two-dot price.
    return String(value == null ? '' : value)
      .replace(/[^\d.,]/g, '')
      .replace(/\./g, '')
      .replace(',', '.');
  }

  _getValidatedUrlParams() {
    const urlParams = new URLSearchParams(window.location.search);
    const rawQty = urlParams.get('qty');
    const rawSub = urlParams.get('sub');

    let qty = 1;
    if (rawQty && !isNaN(parseInt(rawQty, 10))) {
      qty = parseInt(rawQty, 10);
    }

    let sub = 0;
    if (rawSub && !isNaN(parseInt(rawSub, 10))) {
      sub = parseInt(rawSub, 10);
    }

    return { qty, sub };
  }

  _createDeliveryTime(transitMin, transitMax) {
    return {
      '@type': 'ShippingDeliveryTime',
      businessDays: {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: ['https://schema.org/Monday', 'https://schema.org/Tuesday', 'https://schema.org/Wednesday', 'https://schema.org/Thursday', 'https://schema.org/Friday'],
      },
      cutoffTime: '13:00+01:00', // Berlin time (CET)
      handlingTime: {
        '@type': 'QuantitativeValue',
        minValue: 0,
        maxValue: 1,
        unitCode: 'DAY',
      },
      transitTime: {
        '@type': 'QuantitativeValue',
        minValue: transitMin,
        maxValue: transitMax,
        unitCode: 'DAY',
      },
    };
  }

  _getShippingDetails(sub) {
    const config = {
      sub0: [
        { country: 'DE', min: '0.00', max: '34.99', rate: '3.90', transit: [1, 2] },
        { country: 'DE', min: '35.00', max: null, rate: '0.00', transit: [1, 2] },
        { country: 'AT', min: '0.00', max: '69.99', rate: '4.98', transit: [1, 4] },
        { country: 'AT', min: '70.00', max: null, rate: '0.00', transit: [1, 4] },
      ],
      sub1: [
        { country: 'DE', rate: '0.00', transit: [1, 2] },
        { country: 'AT', rate: '4.98', transit: [1, 4] },
      ],
    };

    // #28: sub is a parsed number, so compare against 1 (not the string '1', which
    // never matched and always fell back to the one-time sub0 shipping details).
    const details = config[sub === 1 ? 'sub1' : 'sub0'];

    return details.map(({ country, min, max, rate, transit }) => ({
      '@type': 'OfferShippingDetails',
      shippingDestination: { '@type': 'DefinedRegion', addressCountry: country },
      ...(min && {
        eligibleTransactionVolume: {
          '@type': 'PriceSpecification',
          minPrice: min,
          ...(max && { maxPrice: max }),
          priceCurrency: 'EUR',
        },
      }),
      shippingRate: { '@type': 'MonetaryAmount', value: rate, currency: 'EUR' },
      deliveryTime: this._createDeliveryTime(transit[0], transit[1]),
    }));
  }
}
